import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import type { AgentConnectionPayload, AgentDeviceRequestPayload } from "@/lib/agent/types";
import { apiClient } from "@/lib/api/client";
import { Check, Copy, KeyRound, ShieldCheck, Terminal, Trash2, X } from "lucide-react";
import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

const SCOPE_COPY: Record<string, string> = {
  "carriers:read": "View connected carriers",
  "rates:read": "Request shipping rates",
  "shipments:read": "View shipments",
  "tracking:read": "Track shipments",
  "bookings:write": "Book and cancel shipments",
};

const LOGIN_COMMAND = "npx @shippeek/cli auth login";

function formatTimestamp(value: string | null): string {
  if (!value) return "Never";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Unknown";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(parsed);
}

function Connections({ connections, onRevoke, revokingId }: {
  connections: AgentConnectionPayload[];
  onRevoke: (connection: AgentConnectionPayload) => void;
  revokingId: string | null;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle><h2>Agent connections</h2></CardTitle>
        <CardDescription>Dedicated credentials are separate from your primary API key and can be revoked at any time.</CardDescription>
      </CardHeader>
      <CardContent>
        {connections.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center">
            <KeyRound aria-hidden="true" className="mx-auto size-5 text-muted-foreground" />
            <p className="mt-3 text-sm font-medium">No agents connected yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Run the login command to connect your first agent.</p>
          </div>
        ) : (
          <ul className="divide-y divide-border" aria-label="Agent connections">
            {connections.map((connection) => {
              const active = !connection.revokedAt;
              return (
                <li key={connection.id} className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-medium">{connection.clientName}</p>
                      <span className={active
                        ? "rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300"
                        : "rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"}
                      >
                        {active ? "Active" : "Revoked"}
                      </span>
                    </div>
                    <p className="mt-1 truncate font-mono text-xs text-muted-foreground">{connection.tokenPrefix}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Connected {formatTimestamp(connection.createdAt)} · Last used {formatTimestamp(connection.lastUsedAt)}
                    </p>
                  </div>
                  {active ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="min-h-10 self-start text-destructive hover:text-destructive sm:self-center"
                      disabled={revokingId === connection.id}
                      onClick={() => onRevoke(connection)}
                    >
                      <Trash2 aria-hidden="true" />
                      {revokingId === connection.id ? "Revoking…" : "Revoke"}
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export default function AgentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code")?.trim() ?? "";
  const [manualCode, setManualCode] = React.useState("");
  const [request, setRequest] = React.useState<AgentDeviceRequestPayload | null>(null);
  const [connections, setConnections] = React.useState<AgentConnectionPayload[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [action, setAction] = React.useState<"approving" | "denying" | null>(null);
  const [outcome, setOutcome] = React.useState<"approved" | "denied" | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [revokingId, setRevokingId] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);

  const loadConnections = React.useCallback(async () => {
    const data = await apiClient.getAgentConnections();
    setConnections(data);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const requestPromise = code ? apiClient.getAgentDeviceRequest(code) : Promise.resolve(null);
    Promise.all([requestPromise, apiClient.getAgentConnections()])
      .then(([deviceRequest, agentConnections]) => {
        if (cancelled) return;
        setRequest(deviceRequest);
        setConnections(agentConnections);
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : "Unable to load agent connections.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [code]);

  const submitManualCode = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const compact = manualCode.trim();
    if (!compact) return;
    navigate(`/agent?code=${encodeURIComponent(compact)}`);
  };

  const approve = async () => {
    if (!request || action) return;
    setAction("approving");
    setError(null);
    try {
      await apiClient.approveAgentDeviceRequest(request.userCode);
      setOutcome("approved");
    } catch (approvalError) {
      setError(approvalError instanceof Error ? approvalError.message : "Unable to approve this request.");
    } finally {
      setAction(null);
    }
  };

  const deny = async () => {
    if (!request || action) return;
    setAction("denying");
    setError(null);
    try {
      await apiClient.denyAgentDeviceRequest(request.userCode);
      setOutcome("denied");
    } catch (denialError) {
      setError(denialError instanceof Error ? denialError.message : "Unable to deny this request.");
    } finally {
      setAction(null);
    }
  };

  const revoke = async (connection: AgentConnectionPayload) => {
    if (!window.confirm(`Revoke ${connection.clientName}? It will immediately lose access to Shippeek.`)) return;
    setRevokingId(connection.id);
    setError(null);
    try {
      await apiClient.revokeAgentConnection(connection.id);
      await loadConnections();
    } catch (revokeError) {
      setError(revokeError instanceof Error ? revokeError.message : "Unable to revoke this connection.");
    } finally {
      setRevokingId(null);
    }
  };

  const copyCommand = async () => {
    try {
      await navigator.clipboard.writeText(LOGIN_COMMAND);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setError("Unable to copy automatically. Select and copy the command instead.");
    }
  };

  const requestResolved = outcome || (request?.status === "approved" ? "approved" : request?.status === "denied" ? "denied" : null);

  return (
    <main className="min-h-screen bg-muted/30 px-4 py-8 sm:py-12">
      <div className="mx-auto w-full max-w-2xl space-y-6">
        <header className="flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 rounded-md text-sm font-semibold outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <img src="/shippeek-mark.svg" alt="" className="size-8" />
            Shippeek
          </Link>
          <Link to="/" className="inline-flex min-h-11 items-center rounded-md px-2 text-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring">
            Back to workspace
          </Link>
        </header>

        {code ? (
          <Card>
            <CardHeader>
              <div className="mb-2 flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <ShieldCheck aria-hidden="true" className="size-5" />
              </div>
              <CardTitle><h1>Connect an agent</h1></CardTitle>
              <CardDescription>Review the request before giving this agent access to your Shippeek workspace.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {loading ? (
                <p role="status" className="text-sm text-muted-foreground">Loading connection request…</p>
              ) : request ? (
                <>
                  <div className="rounded-lg border border-border bg-background p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold">{request.clientName}</p>
                        <p className="mt-1 text-xs text-muted-foreground">Connection code</p>
                      </div>
                      <code className="rounded-md bg-muted px-2.5 py-1.5 text-sm font-semibold tracking-wider">{request.userCode}</code>
                    </div>
                  </div>

                  <section aria-labelledby="requested-access-heading">
                    <h2 id="requested-access-heading" className="text-sm font-medium">Requested access</h2>
                    <ul className="mt-3 space-y-2">
                      {request.scopes.map((scope) => (
                        <li key={scope} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Check aria-hidden="true" className="size-4 text-emerald-600" />
                          {SCOPE_COPY[scope] ?? scope}
                        </li>
                      ))}
                    </ul>
                  </section>

                  <p className="text-xs leading-5 text-muted-foreground">
                    This creates a separate, revocable credential. Your password and primary API key are never shared. If your trial has not started, it begins when the CLI completes this connection.
                  </p>

                  {requestResolved ? (
                    <div
                      role="status"
                      aria-live="polite"
                      className={requestResolved === "approved"
                        ? "rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200"
                        : "rounded-lg border border-border bg-muted p-4 text-sm text-muted-foreground"}
                    >
                      {requestResolved === "approved"
                        ? "Request approved. Return to your terminal—your agent will finish connecting automatically."
                        : "Request denied. The agent cannot access this workspace."}
                    </div>
                  ) : (
                    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                      <Button type="button" variant="outline" size="lg" className="min-h-11" disabled={Boolean(action)} onClick={deny}>
                        <X aria-hidden="true" />
                        {action === "denying" ? "Denying…" : "Deny request"}
                      </Button>
                      <Button type="button" size="lg" className="min-h-11" disabled={Boolean(action)} onClick={approve}>
                        <ShieldCheck aria-hidden="true" />
                        {action === "approving" ? "Connecting…" : "Connect agent"}
                      </Button>
                    </div>
                  )}
                </>
              ) : null}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <div className="mb-2 flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Terminal aria-hidden="true" className="size-5" />
              </div>
              <CardTitle><h1>Connect your coding agent</h1></CardTitle>
              <CardDescription>Sign in from the Shippeek CLI, approve once here, then keep prompting your agent.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center gap-2 rounded-lg border border-border bg-background p-3">
                <code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap text-sm">{LOGIN_COMMAND}</code>
                <Button type="button" variant="ghost" size="icon" className="min-h-10 min-w-10" onClick={copyCommand} aria-label="Copy login command">
                  {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
                </Button>
              </div>
              <p role="status" aria-live="polite" className="min-h-5 text-xs text-muted-foreground">
                {copied ? "Login command copied." : "The CLI opens this page with a one-time connection code."}
              </p>

              <form className="space-y-2" onSubmit={submitManualCode}>
                <label htmlFor="agent-code" className="text-sm font-medium">Enter a connection code</label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    id="agent-code"
                    value={manualCode}
                    onChange={(event) => setManualCode(event.target.value)}
                    placeholder="ABCD-2345"
                    autoComplete="one-time-code"
                    className="h-11 min-w-0 flex-1 rounded-lg border border-input bg-background px-3 font-mono text-sm uppercase tracking-wider outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
                  />
                  <Button type="submit" size="lg" className="min-h-11" disabled={!manualCode.trim()}>Review request</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {error ? (
          <div role="alert" aria-live="assertive" className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        ) : null}

        <Connections connections={connections} onRevoke={revoke} revokingId={revokingId} />
      </div>
    </main>
  );
}
